<template>
  <div id="video-basic-layout-paddings" class="gs-app gs-video-library">
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <div class="w-full">
      <library-header
        :total="totalVideosCount"
        :published="null"
        :category-options="categoryList"
        :category="selectedCategory"
        :order="selectedOrder"
        :order-options="timeOptions"
        :search="searchVideoName"
        @add="createVideo()"
        @update:category="categoryChange"
        @update:order="handleOrderChange"
        @update:search="handleSearchUpdate"
      />

      <v-wait for="videos">
        <div slot="waiting" class="gs-video-grid">
          <video-card-skeleton v-for="n in 6" :key="n" />
        </div>

        <div v-if="videosLoaded && videos.length > 0">
          <div class="gs-video-grid">
            <video-card
              v-for="video in videos"
              :key="video.id"
              :video="video"
              :category-name="categoryNameFor(video)"
              @preview="openPreview"
              @edit="viewVideo(video.id)"
              @analytics="$router.push({ name: 'videoAnalytics', params: { videoId: video.id } })"
              @share="getVideo(video)"
              @clone="cloneVideo(video.id)"
              @delete="confirmDelete(video)"
            />
          </div>

          <infinite-loading
            ref="infiniteLoading"
            @infinite="infiniteHandler"
            spinner="spiral"
          >
            <div class="text-red" slot="no-more">
              {{ $t('frontend_groovevideo_no_more_videos') }}
            </div>
            <div class="text-red" slot="no-results">
              {{ $t('frontend_groovevideo_no_more_videos') }}
            </div>
          </infinite-loading>
        </div>
      </v-wait>

      <library-empty-state
        v-if="videosLoaded && videos.length === 0"
        :filtered="isFiltered"
        @add="createVideo()"
        @clear="clearFilters()"
      />

      <!-- No connected storage modal -->
      <add-storage-modal ref="addStorageModal"/>

      <share-video-modal
        :embed="embedVideo"
        :publishes-left="publishesLeft"
        :open="shareOpen"
        @close="shareOpen = false"
        @isLoading="isLoading = $event"
        @updatePublishLimit="updatePublishLimit($event)"
        @updateHostedPageSettings="updateHostedPageSettings"
        @checkDownloadable="checkDownloadable"
        @copied="onShareCopied"
        @copy-failed="onShareCopyFailed"
      />

      <preview-video-modal
        :video="videoPreview"
        :open="previewOpen"
        @close="previewOpen = false"
        @edit="viewVideo(videoPreview.id)"
        @share="previewShare(videoPreview)"
      />

      <delete-video-dialog
        v-model="deleteDialogOpen"
        :video="videoToDelete"
        @confirm="confirmDeleteVideo"
      />

      <upgrade-modal ref="upgradeModal" />

      <add-video-drawer
        ref="addVideoDrawer"
        :open="addOpen"
        :categories="categoryList.slice(1)"
        :remaining-videos="$store.getters.maxVideos === -1 ? Infinity : maxVideosAllowed"
        :publishes-left="publishesLeft"
        @close="addOpen = false"
        @created="id => { addOpen = false; $router.push({ name: 'editVideo', params: { id } }); }"
        @upgrade="$refs.upgradeModal.open()"
        @new-category="addNewCategoryOpen = true"
      />

      <add-new-category
        v-if="addNewCategoryOpen"
        :categories="categories"
        @close-modal="addNewCategoryOpen = false"
        @on-save="onCategorySaved($event)"
      />

      <portal to="headerContent">
        <div class="flex items-center">
          <gs-button variant="outline" @click="createVideo()">
            Add video
          </gs-button>
        </div>
      </portal>
    </div>
  </div>
</template>

<style>
.gs-video-library {
  height: calc(100vh - 4rem);
  overflow-y: auto;
  overflow-x: hidden;
}

.gs-video-library .gs-library-header {
  margin: 20px 20px 0;
}

.gs-video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 20px;
}
</style>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Swal from 'sweetalert2';
import Loading from 'vue-loading-overlay';
import Dropmenu from '@groovepages/gd-ui-kit/components/DropMenu.vue';
import InfiniteLoading from 'vue-infinite-loading';
import Notify from '@groovepages/gd-lib/notify';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import moment from 'moment';
import axios from 'axios';
import Video from '@/models/Video';
import EmbedPageSettings from '@/models/EmbedPageSettings';
import themeConfig from '@/theme/themeConfig.js';
import 'vue-loading-overlay/dist/vue-loading.css';
import Snapshot from '@/assets/images/icons/Focus.svg';
import store from '@/state';
import Storage from '@/models/Storage';
import Encoder from '@/models/Encoder';
import Category from '@/models/Category';
import VideoPlayer from './VideoPlayer';
import helpers from '@/mixins/upgradeHelper.js';
import AddStorageModal from '@/components/AddStorageModal';
import VerticalTabs from '@/components/VerticalTabs';
import limitHelper from '@/mixins/limitHelper.js';
import PublishVideo from '@/components/PublishVideo';
import GreenTickIcon from '@/assets/images/icons/greentick.svg';
import UpgradeModal from '@/components/upgradeModal';
import { formatDuration } from '@/utils/duration';
import { flattenCategories } from '@/utils/categories';
import LibraryHeader from '@/components/library/LibraryHeader.vue';
import VideoCard from '@/components/library/VideoCard.vue';
import VideoCardSkeleton from '@/components/library/VideoCardSkeleton.vue';
import LibraryEmptyState from '@/components/library/LibraryEmptyState.vue';
import PreviewVideoModal from '@/components/library/PreviewVideoModal.vue';
import ShareVideoModal from '@/components/library/ShareVideoModal.vue';
import AddVideoDrawer from '@/components/library/AddVideoDrawer.vue';
import DeleteVideoDialog from '@/components/library/DeleteVideoDialog.vue';
import { deleteVideo as deleteVideoRequest, muxError } from '@/services/muxApi';
import AddNewCategory from '@/pages/videos/AddNewCategory';

export default {
  middleware: 'auth',
  layout: 'defaultInfiniteScroll',

  metaInfo() {
    return { title: 'My Videos' };
  },
  props: {
    activity: '',
  },
  mixins: [helpers, limitHelper],
  data() {
    return {
      selectedOrder: 'created_at-desc',
      selectedCategory: 0,
      categories: {},
      categoryList: [{ id: 0, category: this.$t('frontend_all_categories'), user_id: 0 }],
      rerenderComponent: true,
      tableComponentKey: 0,
      funnel: null,
      funnels: [],
      video: null,
      videosLoaded: false,
      videos: [],
      maxVideosAllowed: 0,
      publishesLeft: 0,
      itemsPerPage: 5,
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      embeddedCode: '',
      // `v-model` writes the new value before `@change` fires, so the values
      // the server last accepted have to be kept separately for a revert.
      lastSavedHostedPageSettings: { autoplay: 0, allow_download: 0 },
      embedVideo: {
        is_published: 0,
        video_embed: '',
        hosted_page_link: '',
        hosted_page_settings: {
          autoplay: 0,
          allow_download: 0,
        },
        video_source: '',
        video_id: '',
        video_source_type: '',
        download_available: 0,
      },
      currentTab: 'frontend_video_player',
      page: 1,
      limit: 12,
      hasNext: true,
      videoPreview: null,
      videoPlayerComponentKey: null,
      searchVideoName: '',
      scrollbarSettings: {
        maxScrollbarLength: 60,
      },
      maxHeight: { 'max-height': '100px' },
      timeout: null,
      maxAllowed: 0,
      maxPublishAllowed: 0,
      totalVideosCount: 0,
      shareOpen: false,
      previewOpen: false,
      deleteDialogOpen: false,
      videoToDelete: null,
      addOpen: false,
      addNewCategoryOpen: false,
    };
  },
  validations: {},
  components: {
    Loading,
    Snapshot,
    Dropmenu,
    InfiniteLoading,
    VuePerfectScrollbar,
    VideoPlayer,
    AddStorageModal,
    Treeselect,
    VerticalTabs,
    PublishVideo,
    GreenTickIcon,
    UpgradeModal,
    LibraryHeader,
    VideoCard,
    VideoCardSkeleton,
    LibraryEmptyState,
    PreviewVideoModal,
    ShareVideoModal,
    AddVideoDrawer,
    DeleteVideoDialog,
    AddNewCategory,
  },
  computed: {
    catOptions() {
      return [
        {
          id: 1,
          name: this.$t('frontend_groovevideo_all_categories'),
        },
        {
          id: 2,
          name: this.$t('frontend_groovevideo_media'),
        },
        {
          id: 3,
          name: this.$t('frontend_groovevideo_education'),
        },
        {
          id: 4,
          name: this.$t('frontend_groovevideo_entertainment'),
        },
      ];
    },
    timeOptions() {
      return [
        {
          value: 'name-asc',
          text: this.$t('frontend_groovevideo_timeoptions_name_asc'),
        },
        {
          value: 'name-desc',
          text: this.$t('frontend_groovevideo_timeoptions_name_desc'),
        },
        {
          value: 'created_at-asc',
          text: this.$t('frontend_groovevideo_timeoptions_first_created'),
        },
        {
          value: 'created_at-desc',
          text: this.$t('frontend_groovevideo_timeoptions_last_created'),
        },
        {
          value: 'updated_at-asc',
          text: this.$t('frontend_groovevideo_timeoptions_first_updated'),
        },
        {
          value: 'updated_at-desc',
          text: this.$t('frontend_groovevideo_timeoptions_last_updated'),
        },
      ];
    },
    admin() {
      return this.$store.state.user.admin;
    },
    playerColor() {
      if (
        typeof this.videoPreview.settings.color !== 'undefined' &&
        typeof this.videoPreview.settings.color.primary !== 'undefined' &&
        typeof this.videoPreview.settings.color.secondary !== 'undefined'
      ) {
        return {
          '--player-primary-color': this.videoPreview.settings.color.primary,
          '--player-secondary-color': this.videoPreview.settings.color
            .secondary,
        };
      }
      return {
        '--player-primary-color': 'rgba(253, 100, 129, 0.8)',
        '--player-secondary-color': 'rgba(255,255,255, 1)',
      };
    },
    vjsClass() {
      if(this.videoPreview.settings.theme == null || this.videoPreview.settings.theme == " ") {
        return 'vjs-theme-default';
      }
      if(this.videoPreview.settings.theme != " ") {
        return this.videoPreview.settings.theme;
      } 
      return '';
    },
    videoLimitReached() {
      return this.maxAllowed !== -1 && this.maxVideosAllowed <= 0;
    },
    tabs() {
      return [
        {
          text: this.$t('frontend_video_player'),
          value: 'frontend_video_player',
        },
        {
          text: this.$t('frontend_groovevideo_hosted_page'),
          value: 'frontend_groovevideo_hosted_page',
        },
        {
          text: this.$t('frontend_video_source_file_url'),
          value: 'frontend_video_source_file_url',
        },
      ];
    },
    isFiltered() {
      return this.selectedCategory !== 0 || this.searchVideoName !== '';
    },
  },
  async created() {
    this.showLoader();

    this.$root.$on('open-add-video', this.openAddVideo);
    if (this.$route.query.add === '1') {
      this.openAddVideo();
      this.$router.replace({ query: {} });
    }

    if(this.activity == 'encoding'){
      Notify.success(this.$t('frontend_groovevideo_video_uploaded_encoding'), '', '',3000);
    }
    this.$wait.start('videos');
    const self = this;
    this.maxAllowed = this.$store.getters.maxVideos;
    this.maxPublishAllowed = this.$store.getters.maxPublishVideos;

    // Get video category
    this.categories = await Category.$get();
    this.categories.forEach((category) => {
      self.categoryList.push(category);
    });


    // Get filters based on user's previous choice
    this.getFilters();
    this.videos = await this.loadVideos();

    // Fetching remaining limits
    this.maxVideosAllowed = await this.getMaxAllowedVideos();
    this.publishesLeft = await this.getRemainingPublishLimit();

    this.$wait.end('videos');
    this.videosLoaded = true;
    if (this.videosLoaded && this.videos.length > 0) {
      // const container = document.getElementById('groovesellCont');
      // if(container) {
      //   container.classList.add('groovevideo-listing-page-container');
      // }
    }
    this.youtubeThumbnail();
    this.hideLoader();
  },
  async mounted() {
    this.youtubeThumbnail();
  },

  beforeDestroy() {
    this.$root.$off('open-add-video', this.openAddVideo);
  },

  destroyed() {
    if (this.videosLoaded && this.videos.length > 0) {
      // const container = document.getElementById('groovesellCont');
      // if(container) {
      //   container.classList.remove('groovevideo-listing-page-container');
      // }
    }
  },

  methods: {
    async loadVideos() {
      if (this.hasNext) {
        const video = await Video.where('category', this.selectedCategory)
          .where('search', this.searchVideoName)
          .orderBy(this.selectedOrder)
          .page(this.page)
          .limit(this.limit)
          .get();
        if (video.meta.last_page === video.meta.current_page) {
          this.hasNext = false;
        } else {
          this.page += 1;
        }
        if (typeof video.meta.total === 'number') {
          this.totalVideosCount = video.meta.total;
        }
        return video.data;
      }
      return [];
    },

    normalizer(node) {
      return {
        id: node.id,
        label: node.category,
        children: node.child && node.child.length ? node.child : undefined,
        user_id: node.user_id
      };
    },

    async checkDownloadable () {
      if (this.embedVideo.download_available === 0) {
        this.embedVideo.hosted_page_settings.allow_download = 0;
      } else {
        await this.updateHostedPageSettings();
      }
    },

    getSourceTypeName(video) {
      const type = video.video_source_type;
      let name = this.$t('frontend_groovevideo_videos_index_mp4_url');
      
      if(type === 1) {
        name = video.is_local === 1 ? this.$t('frontend_groovevideo_videos_index_uploaded') : this.$t('frontend_groovevideo_videos_index_own_uploaded');
      } else if(type === 2) {
        name = this.$t('frontend_groovevideo_videos_index_youtube_url');
      } else if (type === 4) {
        name = 'Mux';
      }

      return name;
    },

    getDuration(duration) {
      return formatDuration(duration);
    },

    async orderChange(e) {
      this.showLoader();
      this.setInitialPage();
      this.videos = await this.loadVideos();
      this.youtubeThumbnail();
      this.$refs.infiniteLoading?.stateChanger.reset();
      this.updateFiltersStorage();

      this.hideLoader();
    },
    handleOrderChange(value) {
      this.selectedOrder = value;
      this.orderChange();
    },
    handleSearchUpdate(value) {
      this.searchVideoName = value;
      this.orderChange();
    },
    clearFilters() {
      this.selectedCategory = 0;
      this.searchVideoName = '';
      this.orderChange();
    },
    categoryNameFor(video) {
      if (!video || !video.category_id) return '';
      const match = flattenCategories(this.categoryList).find(
        (cat) => cat.id === video.category_id
      );
      return match ? match.name : '';
    },
    async categoryChange(value) {
      this.showLoader();
      this.setInitialPage();
      this.selectedCategory = value;
      this.videos = await this.loadVideos();
      this.youtubeThumbnail();
      this.$refs.infiniteLoading?.stateChanger.reset();
      this.updateFiltersStorage();

      this.hideLoader();
    },
    searchVideoFilter() {
      clearTimeout(this.timeout);
      let self = this;
      this.timeout = setTimeout(function () {
          self.orderChange();
      }, 1000);
    },
    setInitialPage() {
      this.page = 1;
      this.hasNext = true;
    },

    // Get filters from local storage
    getFilters() {
      const filters = localStorage.getItem('video_filters') 
        ? JSON.parse(localStorage.getItem('video_filters'))
        : null;
      
      if(filters && filters.category && this.categoryList.find((e) => e.id == filters.category)) {
        this.selectedCategory = filters.category;
      }

      if(filters && filters.orderBy) {
        this.selectedOrder = filters.orderBy ?? 'created_at-desc';
      }
    },

    // Set filters in local storage for future reference
    updateFiltersStorage() {
      localStorage.setItem('video_filters', JSON.stringify({
        category: this.selectedCategory,
        orderBy: this.selectedOrder
      }));
    },
    backgroundImageUrl(url) {
      return `url("${url}")`;
    },
    // youtube thumbnail Image setting
    youtubeThumbnail(start = 0) {
      for (let i = start; i < this.videos.length; i++) {
        if (
          this.videos[i].video_source_type === 2 &&
          (this.videos[i].settings.splashImg === '' ||
            this.videos[i].settings.splashImg == null) &&
          this.videos[i].video_source
        ) {
          const vidSrc = this.videos[i].video_source;
          const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
          const match = vidSrc.match(regExp);
          const youtubeID = match && match[7].length === 11 ? match[7] : false;
          this.videos[
            i
          ].settings.splashImg = `https://img.youtube.com/vi/${youtubeID}/sddefault.jpg`;
        } else if (
          this.videos[i].video_source_type === 3 &&
          (this.videos[i].settings.splashImg === '' ||
            this.videos[i].settings.splashImg == null)
        ) {
          this.videos[i].settings.splashImg =
            'https://img.youtube.com/vi/false/sddefault.jpg';
        }
      }
    },

    updatePublishLimit(data) {
      this.embedVideo.is_published = data.isPublished;

      const index = this.videos.findIndex((v) => v.id === data.videoId);
      if(index >= 0) {
        this.videos[index].is_published = data.isPublished;
        this.publishesLeft = data.isPublished ? this.publishesLeft -1 : this.publishesLeft + 1;
      }
    },

    onCopyEmbeddedCode() {
      Notify.success(this.$t('frontend_groovevideo_videos_wait_video_embed'));
    },

    onErrorEmbeddedCode() {
      Notify.fail(
        this.$t('frontend_groovevideo_videos_wait_video_unable_to_copy')
      );
    },

    onCopyVideoPageUrl() {
      Notify.success(
        this.$t('frontend_groovevideo_videos_wait_copy_video_page_url')
      );
    },

    onErrorVideoPageUrl() {
      Notify.fail(
        this.$t(
          'frontend_groovevideo_videos_wait_unable_to_copy_video_page_url'
        )
      );
    },

    onCopySourceFileUrl() {
      Notify.success(
        this.$t('frontend_groovevideo_videos_wait_copy_source_url')
      );
    },

    onErrorSourceFileUrl() {
      Notify.fail(
        this.$t(
          'frontend_groovevideo_videos_wait_video_unable_to_copy_source_url'
        )
      );
    },

    createVideo() {
      this.$root.$emit('open-add-video');
    },

    openAddVideo() {
      this.addOpen = true;
    },

    async onCategorySaved(categoryId) {
      this.addNewCategoryOpen = false;
      this.categories = await Category.$get();
      this.categoryList = [
        { id: 0, category: this.$t('frontend_all_categories'), user_id: 0 },
        ...this.categories,
      ];
      if (categoryId && this.$refs.addVideoDrawer) {
        this.$refs.addVideoDrawer.selectCategory(categoryId);
      }
    },

    // Same contract as the editor's Share panel: the loader must always come
    // down and a rejected request must put the toggle back.
    async updateHostedPageSettings() {
      this.showLoader();
      const settings = this.embedVideo.hosted_page_settings;
      const previous = this.lastSavedHostedPageSettings;
      const attempted = {
        autoplay: settings.autoplay ? 1 : 0,
        allow_download: settings.allow_download ? 1 : 0,
      };
      const hostedSettings = new EmbedPageSettings(attempted);

      const index = this.videos
        .map((item) => item.id)
        .indexOf(this.embedVideo.video_id);

      try {
        const savedSettings = await hostedSettings
          .for(this.videos[index])
          .save();

        this.videos[index].hosted_page_settings = savedSettings.data;
        this.lastSavedHostedPageSettings = attempted;
        this.embedVideo.hosted_page_link = this.videos[index].hostedPageLink;
      } catch (error) {
        this.$set(settings, 'autoplay', previous.autoplay);
        this.$set(settings, 'allow_download', previous.allow_download);
        Notify.fail('We could not save the hosted page settings. Please try again.');
      } finally {
        this.hideLoader();
      }
    },

    viewVideo(id) {
      this.$router.push({ name: 'editVideo', params: { id } });
    },

    async getVideo(video) {
      if ((video.video_source_type !== 2 || !video.is_uploading) && (video.is_available == 1 || (video.is_local !== 1 && video.video_source !== '')) && typeof video.temp_video_source === 'undefined') {
        if (
          (video.video_embed &&
          video.video_embed !== '' &&
          video.video_source &&
          video.video_source !== '' &&
          video.hostedPageLink) || (
            video.is_local === 1 && video.video_stream_source !== null  && video.video_stream_source !== ''
          )
        ) {
          this.embedVideo.is_published = video.is_published;
          this.embedVideo.video_id = video.id;
          this.embedVideo.video_embed = video.video_embed;
          this.embedVideo.hosted_page_link = video.hostedPageLink;
          this.embedVideo.hosted_page_settings = video.hosted_page_settings;
          this.lastSavedHostedPageSettings = {
            autoplay: video.hosted_page_settings ? video.hosted_page_settings.autoplay : 0,
            allow_download: video.hosted_page_settings ? video.hosted_page_settings.allow_download : 0,
          };
          this.embedVideo.video_source_type = video.video_source_type;
          this.embedVideo.download_available = video.video_source_type === 1 && (!video.is_local || video.is_downloadable);

          if(!video.is_uploading) {
            this.embedVideo.show_source_url = true;
            this.embedVideo.source_file_url = video.is_local && video.video_stream_source && video.is_downloadable !== 1 ? video.video_stream_source : video.video_source;
          } else {
            this.embedVideo.show_source_url = false;
            this.embedVideo.source_file_url = '';
          }

          this.shareOpen = true;
          this.hideLoader();
        } else {
          this.showAlertBox();
        }
      } else {
        this.videoProcessingAlert(video.video_source_type);
      }
    },

    onShareCopied(type) {
      if (type === 'embed') return this.onCopyEmbeddedCode();
      if (type === 'source') return this.onCopySourceFileUrl();
      return this.onCopyVideoPageUrl();
    },

    onShareCopyFailed(type) {
      if (type === 'embed') return this.onErrorEmbeddedCode();
      if (type === 'source') return this.onErrorSourceFileUrl();
      return this.onErrorVideoPageUrl();
    },

    showAlertBox() {
      Swal.fire({
        type: 'error',
        title: this.$t('frontend_groovevideo_error_title'),
        text: this.$t('frontend_groovevideo_video_index_upload_embed_code'),
        reverseButtons: true,
        confirmButtonText: 'Ok',
        cancelButtonText: this.$t('frontend_cancel'),
      });
    },
    videoProcessingAlert(type) {
      Swal.fire({
        title: this.$t('frontend_groovevideo_warning_title'),
        text: type === 2 ? this.$t('frontend_groovevideo_step4_video_uploading_to_yt_info') : this.$t('frontend_groovevideo_step4_video_encoding_info'),
        confirmButtonText: 'Ok'
      });
    },

    openPreview(video) {
      if (video.is_available == 1 || (video.is_local !== 1 && video.video_source !== '')) {
        if ((video.is_local == 1 && video.video_stream_source != '' && video.video_stream_source != null) || video.video_source) {
          this.videoPreview = video;
          this.previewOpen = true;
        } else {
          this.showAlertBox();
        }
      } else {
        this.videoProcessingAlert(video.video_source_type);
      }
    },

    previewShare(video) {
      this.previewOpen = false;
      this.getVideo(video);
    },

    confirmDelete(video) {
      this.videoToDelete = video;
      this.deleteDialogOpen = true;
    },

    async confirmDeleteVideo(options = {}) {
      if (this.videoToDelete) {
        await this.deleteVideo(this.videoToDelete.id, options);
        this.videoToDelete = null;
      }
    },

    async deleteVideo(id, { deleteFromMux = false } = {}) {
      this.showLoader();
      try {
        const mux = await deleteVideoRequest(id, { deleteFromMux });
        if (mux && mux.result === 'deleted') Notify.success(mux.message);
        else if (mux) Notify.fail(mux.message);
      } catch (error) {
        this.hideLoader();
        Notify.fail(muxError(error, 'We could not delete the video. Please try again.').message);
        return;
      }
      this.videos = [];
      this.setInitialPage();
      this.$wait.start('videos');
      this.videos = await this.loadVideos();
      this.$wait.end('videos');
      this.youtubeThumbnail();
      this.maxVideosAllowed += 1;
      this.hideLoader();
    },

    async cloneVideo(id) {
      if (this.videoLimitReached) {
        return this.showVideoLimitReachedAlert();
      }
      this.showLoader();
      try {
        const video = await Video.$find(id);
        await Video.custom(video, 'clone').$first();
        this.videos = [];
        this.setInitialPage();
        this.$wait.start('videos');
        this.videos = await this.loadVideos();
        this.$wait.end('videos');
        this.youtubeThumbnail();
        this.maxVideosAllowed -= 1;
        Notify.success('Video cloned');
      } catch (e) {
        // The API only clones owned YouTube-sourced videos; legacy uploads are refused.
        Notify.fail((e.response && e.response.data && e.response.data.message) || 'We could not clone this video.');
      } finally {
        this.$wait.end('videos');
        this.hideLoader();
      }
    },

    showVideoLimitReachedAlert() {
      return Swal.fire({
        title: this.$t('frontend_groovevideo_videos_index_upgrade_to_pro_today'),
        text: this.$t('frontend_groovevideo_videos_index_to_unlock_more_videos'),
        type: 'info',
        confirmButtonText: this.$t('frontend_groovevideo_videos_index_see_all'),
        showCancelButton: true,
      }).then(({ value }) => { if (value) this.goToUpgradePage(); });
    },

    infiniteHandler($state) {
      setTimeout(async () => {
        const newVideos = await this.loadVideos();

        if (newVideos && newVideos.length > 0) {
          const { length } = this.videos;
          this.videos = this.videos.concat(newVideos);
          this.youtubeThumbnail(length);
          $state.loaded();
        } else {
          $state.complete();
        }
      }, 1000);
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
    handleTabChange(newTab) {
      this.currentTab = newTab;
    },
  },
};
</script>

<style lang="scss">
.dropdown-button-container {
  display: flex;
  align-items: center;

  .btnx {
    border-radius: 5px 0px 0px 5px;
  }

  .btn-drop {
    border-radius: 0px 5px 5px 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}

#funnels-list-view,
#products-list-view {
  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 200;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .funnelRow:hover .expandIcon {
    display: block !important;
  }
}
.upgradeModal {
  .h-20.flex {
    border-top: 0 !important;
  }
}

.vjs-control-bar,
.cta-optin-form-submit-button.videoBtn,
.cta-optin-form-submit-button.videoBtn:hover,
.cta-optin-form-submit-button.videoBtn:active,
.cta-optin-form-submit-button.videoBtn:focus,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:hover,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:active {
  background-color: var(--player-primary-color) !important;
}
.optin-skip.cta-skip-button:hover,
.cta-skip-banner-button.banner-skip:hover {
  color: var(--player-primary-color) !important;
}
.video-js {
  color: var(--player-secondary-color) !important;
}
.vjs-big-play-button {
  border: 0 !important;
  &:active,
  &:focus {
    outline: none;
  }
  // .vjs-icon-placeholder {
  //   &:before {
  //     margin-top: 3px;
  //   }
  // }
}

</style>

<style scoped>
:deep(.videojs-event-tracking-player-dimensions) {
  height: 0px !important;
  width: 0px !important;
}
.img {
  width: 50px !important;
  position: relative;
  right: -300px;
  opacity: 0.7;
  cursor: pointer;
}
</style>
